import { apiClient } from "./apiClient"
import i18n from "@/utils/i18n"

const CLIENT_FILES_ENDPOINT = "/api/client/files"
const ADMIN_FILES_UPLOAD_ENDPOINT = "/api/admin/files/upload"
const ADMIN_MULTIPART_INITIATE_ENDPOINT = "/api/admin/files/multipart/initiate"
const ADMIN_MULTIPART_COMPLETE_ENDPOINT = "/api/admin/files/multipart/complete"
const ADMIN_MULTIPART_ABORT_ENDPOINT = "/api/admin/files/multipart/abort"

/** POST /api/admin/files/upload response item */
export interface AdminFileUploadResDto {
  id: string
  filename: string
  originalName: string
  mimeType: string
  size: number
  sizeFormatted: string
  createdAt: string
}

/**
 * Admin dosya yükleme - POST /api/admin/files/upload
 * multipart/form-data, field adı: "files"
 * @returns Yüklenen dosyaların FileResDto listesi (id, filename, ...)
 */
export async function uploadAdminFiles(
  files: File[]
): Promise<AdminFileUploadResDto[]> {
  const formData = new FormData()
  files.forEach((file) => formData.append("files", file))

  const { data } = await apiClient.post<{
    success?: boolean
    data?: AdminFileUploadResDto[]
  }>(ADMIN_FILES_UPLOAD_ENDPOINT, formData)

  const items = data?.data ?? data
  return Array.isArray(items) ? items : []
}

/** Single presigned PUT URL returned by /multipart/initiate */
export interface PresignedPart {
  partNumber: number
  url: string
}

export interface InitiateMultipartReq {
  filename: string
  mimeType: string
  size: number
  durationMinutes?: number
}

export interface InitiateMultipartRes {
  uploadId: string
  s3Key: string
  partSize: number
  parts: PresignedPart[]
}

export interface CompletedPart {
  partNumber: number
  etag: string
}

export interface CompleteMultipartReq {
  uploadId: string
  s3Key: string
  originalName: string
  mimeType: string
  size: number
  durationMinutes?: number
  parts: CompletedPart[]
}

/** POST /api/admin/files/multipart/initiate */
export async function initiateMultipartUpload(
  req: InitiateMultipartReq,
): Promise<InitiateMultipartRes> {
  const { data } = await apiClient.post<{
    success?: boolean
    data?: InitiateMultipartRes
  }>(ADMIN_MULTIPART_INITIATE_ENDPOINT, req)
  const payload = data?.data ?? (data as unknown as InitiateMultipartRes)
  if (!payload?.uploadId || !payload?.parts?.length) {
    throw new Error(i18n.t("fileInput.multipartInitiateFailed"))
  }
  return payload
}

/** POST /api/admin/files/multipart/complete */
export async function completeMultipartUpload(
  req: CompleteMultipartReq,
): Promise<AdminFileUploadResDto> {
  const { data } = await apiClient.post<{
    success?: boolean
    data?: AdminFileUploadResDto
  }>(ADMIN_MULTIPART_COMPLETE_ENDPOINT, req)
  const payload = data?.data ?? (data as unknown as AdminFileUploadResDto)
  if (!payload?.id) {
    throw new Error(i18n.t("fileInput.multipartCompleteFailed"))
  }
  return payload
}

/** POST /api/admin/files/multipart/abort — best-effort, never throws */
export async function abortMultipartUpload(req: {
  uploadId: string
  s3Key: string
}): Promise<void> {
  try {
    // Don't toast if abort itself fails — the call is fire-and-forget.
    await apiClient.post(ADMIN_MULTIPART_ABORT_ENDPOINT, req, { skipErrorToast: true })
  } catch {
    // S3 lifecycle (3 days) is the safety net.
  }
}

/** GET /api/client/files/:fileId/download response */
export interface FileDownloadResponse {
  success?: boolean
  status?: number
  data?: {
    downloadUrl: string
    expiresAt?: string
    expiresIn?: number
  }
  message?: string
}

/**
 * Resim ve diğer dosyalar için signed URL alır.
 * GET /api/client/files/:fileId/download → data.downloadUrl (signed, geçici erişim)
 */
export async function getFileDownloadUrl(fileId: string): Promise<string> {
  const response = await apiClient.get<FileDownloadResponse>(
    `${CLIENT_FILES_ENDPOINT}/${fileId}/download`
  )
  const data = response.data?.data ?? (response.data as { downloadUrl?: string } | undefined)
  const url = data?.downloadUrl
  if (!url) {
    throw new Error(i18n.t("filesApi.downloadUrlMissing"))
  }
  return url
}
